import { useCallback } from 'react';
import { useInstantSearch } from 'react-instantsearch';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import FilterTitle from '@sorare/core/src/components/search/FilterTitle';
import { ExtendedUIState } from '@sorare/core/src/components/search/InstantSearch/types';
import Option from '@sorare/core/src/components/search/Option';
import { useConfigContext } from '@sorare/core/src/contexts/config';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import useVirtualToggle from '@sorare/core/src/hooks/useVirtualToggle';
import { useVirtualToggleManager } from '@sorare/core/src/hooks/useVirtualToggleManager';
import useEvents from '@sorare/core/src/lib/events/useEvents';
import {
  FilterWidget,
  VIRTUAL_TOGGLE_FILTERS,
} from '@sorare/core/src/lib/filters';

export const DEFAULT_VALUE = 'global:all_star';

const FILTER = VIRTUAL_TOGGLE_FILTERS.leagueFilter;

const LeagueFilter = ({ visible }: { visible: boolean }) => {
  const { sport = Sport.FOOTBALL } = useSportContext();
  const {
    so5: {
      footballLeaguesAlgoliaFilters,
      baseballLeaguesAlgoliaFilters,
      nbaLeaguesAlgoliaFilters,
    },
  } = useConfigContext();

  const filters = {
    [Sport.FOOTBALL]: footballLeaguesAlgoliaFilters,
    [Sport.BASEBALL]: baseballLeaguesAlgoliaFilters,
    [Sport.NBA]: nbaLeaguesAlgoliaFilters,
  }[sport];

  const options = Object.entries(filters).filter(([k, v]) => v && k);

  const setVirtualToggle = useVirtualToggleManager();
  const { currentRefinement } = useVirtualToggle<string>({
    name: FILTER.name,
  });
  const track = useEvents();
  const onChange = useCallback(
    (league: string) => {
      const isActive = currentRefinement === league;
      const newValue = isActive ? undefined : league;
      setVirtualToggle({
        [FILTER.name]: newValue,
      });

      if (!isActive) {
        track('Use Market Filter', {
          filterName: FILTER.trackingName,
          filterValue: league,
        });
      }
    },
    [currentRefinement, setVirtualToggle, track]
  );

  if (!visible) return <FilterSection />;

  return (
    <FilterSection>
      {options.map(([k]) => (
        <Option
          key={k}
          label={k}
          active={currentRefinement === k}
          onClick={() => onChange(k)}
          variant="radio"
        />
      ))}
    </FilterSection>
  );
};

const Title = () => {
  const { indexUiState } = useInstantSearch<ExtendedUIState>();
  const selected = !!indexUiState?.virtualToggle?.[FILTER.name];

  return <FilterTitle name={FILTER.title!} selected={selected} />;
};

const widget: FilterWidget = {
  key: FILTER.name,
  type: 'virtualToggle',
  filter: FILTER,
  component: ({ visible }) => <LeagueFilter visible={visible} />,
  title: <Title />,
};

export default widget;
